// src/utils/authHelper.js
import logger from './logger';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const ACCESS_KEY = 'user_access_token';
const REFRESH_KEY = 'user_refresh_token';

let _memoryAccessToken = null;

// ── TWA Detection ─────────────────────────────────────────────────────────────
/**
 * Detects if the app is running in a Trusted Web Activity (Android App).
 * Note: We explicitly exclude 'isStandalone' (PWA) here for production
 * to ensure web browser users stay in Cookie Mode as requested.
 */
export const isTWA = () => {
  try {
    // 1. Direct Referrer (Android TWA)
    const isAndroidTWA = document.referrer?.startsWith('android-app://');
    
    // 2. Persistent Flag (Set during login if tokens were received)
    const hasTWAFlag = localStorage.getItem('twa_mode') === 'true';
    
    // 3. Fallback: If we already have a refresh token, we must stay in token mode
    const hasToken = !!localStorage.getItem(REFRESH_KEY);

    return isAndroidTWA || hasTWAFlag || hasToken;
  } catch {
    return false;
  }
};

// ── Environment ───────────────────────────────────────────────────────────────
export const getEnvironment = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development';
  if (hostname.includes('testing.bookizap.com')) return 'testing';
  if (hostname.includes('bookizap.com')) return 'production';
  return 'development';
};

/**
 * Returns true if token-based auth (localStorage) should be used.
 *
 * ✅ Token mode  : localhost + testing.bookizap.com + TWA (bookizap.com in app)
 * ✅ Cookie mode : bookizap.com in WEB BROWSER / PWA
 */
export const isLocalToRemote = () => {
  const hostname = window.location.hostname;

  // 1. Localhost / Development
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;

  // 2. Local Network IP / ngrok (Development)
  const isIp = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);
  const isNgrok = hostname.includes('.ngrok-free.app') || hostname.includes('.ngrok.io');
  if (isIp || isNgrok) return true;

  // 3. testing.bookizap.com -> Always Token Mode (Broadened to include subdomains)
  if (hostname.includes('testing.bookizap.com')) return true;

  // 4. bookizap.com -> Token Mode ONLY if it's explicitly a TWA
  if (
    (hostname === 'bookizap.com' || hostname === 'www.bookizap.com') &&
    isTWA()
  ) return true;

  // 5. Default: Production Web Browser -> Cookie Mode
  return false;
};

export const getAuthMode = () => isLocalToRemote() ? 'token' : 'cookie';

// ── Token storage ─────────────────────────────────────────────────────────────
export const storeTokens = (accessToken, refreshToken) => {
  // 1. Store Access Token (Memory + localStorage for persistence)
  if (accessToken) {
    _memoryAccessToken = accessToken;
    localStorage.setItem(ACCESS_KEY, accessToken);
  }

  // 2. Store Refresh Token (localhost + testing + TWA → localStorage)
  if (isLocalToRemote()) {
    if (refreshToken) {
      localStorage.setItem(REFRESH_KEY, refreshToken);
    }
    logger.log(`✅ [${getEnvironment().toUpperCase()}${isTWA() ? '+TWA' : ''}] Tokens stored (v2 keys)`);
  } else {
    // bookizap.com web browser → httpOnly cookie (backend manages refresh token)
    logger.log('✅ [PROD-WEB] Access token cached');
  }
};

export const getAccessToken = () => {
  if (!_memoryAccessToken) {
    _memoryAccessToken = localStorage.getItem(ACCESS_KEY);
  }
  return _memoryAccessToken;
};

export const getRefreshToken = () => {
  if (isLocalToRemote()) {
    return localStorage.getItem(REFRESH_KEY);
  }
  return null;
};

export const clearTokens = () => {
  _memoryAccessToken = null;
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  
  // Legacy cleanup
  localStorage.removeItem('bkz_rt');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  
  logger.log('✅ Tokens cleared');
};

// ── Auth state ────────────────────────────────────────────────────────────────
export const hasTokens = () => {
  if (isLocalToRemote()) {
    return !!localStorage.getItem(REFRESH_KEY);
  }
  // bookizap.com web → check auth flag
  return localStorage.getItem('isAuthenticated') === 'true';
};

export const isAuthenticated = () => hasTokens();

// ── Debug ─────────────────────────────────────────────────────────────────────
export const logAuthConfig = () => {
  const config = {
    environment: getEnvironment(),
    hostname: window.location.hostname,
    isTWA: isTWA(),
    authMode: getAuthMode(),
    isLocalToRemote: isLocalToRemote(),
    apiUrl: API_URL,
    hasMemoryToken: !!_memoryAccessToken,
    hasRefreshToken: !!localStorage.getItem(REFRESH_KEY),
  };
  logger.log('🔐 Auth Configuration:', config);
  return config;
};

// ── TWA persistence ───────────────────────────────────────────────────────────
if (typeof window !== 'undefined' && isTWA()) {
  localStorage.setItem('twa_mode', 'true');
}

const authHelper = {
  API_URL, isTWA, isLocalToRemote, getAuthMode, getEnvironment,
  storeTokens, getAccessToken, getRefreshToken, clearTokens,
  hasTokens, isAuthenticated, logAuthConfig,
};

export default authHelper;
